import { Loader } from '@/components/Loader';
import { Button, Text } from '@/components/ui';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { PlusIcon, RefreshCwIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TicketCard } from './components/TicketCard';
import { useState, useMemo, useEffect } from 'react';
import { DatePicker } from '@/components/ui/DatePicker';
import { CreateTicketModal } from './components/CreateTicketModal';
import { UpdateTicketModal } from './components/UpdateTicketModal';
import { useTickets } from '@/hooks/useTickets';
import { useCompanies } from '@/hooks/useCompanies';
import { Ticket } from '@/lib/api/tickets';
import { SelectOption } from '@/components/ui/Select';
import { DateRange } from 'react-day-picker';
import { SyncTicketsModal } from './components/SyncTicketsModal';
import { Pagination } from '@/components/ui/Pagination';
import { useSyncStore } from '@/lib/sync';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function TicketsPage() {
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [hideWithoutHours, setHideWithoutHours] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const { lastTicketsSync } = useSyncStore();

  const { t } = useTranslation();
  const { companies } = useCompanies();

  const { tickets, isLoading, isSaving, pagination, createTicket, updateTicket, deleteTicket } =
    useTickets({
      page: currentPage,
      limit: 9,
      companyId: selectedCompanyId === 'all' ? undefined : selectedCompanyId,
      startDate: dateRange?.from ? dateRange.from.toISOString().split('T')[0] : undefined,
      endDate: dateRange?.to ? dateRange.to.toISOString().split('T')[0] : undefined,
      withHours: hideWithoutHours ? true : undefined,
    });

  const companyOptions: SelectOption[] = useMemo(
    () => [
      { value: 'all', content: t('common.all') },
      ...companies.map((company) => ({
        value: company.id,
        content: company.name,
      })),
    ],
    [companies]
  );

  const handleEditTicket = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setUpdateModalOpen(true);
  };

  const confirmDeleteTicket = async (ticketId: string) => {
    const success = await deleteTicket(ticketId);
    if (success) {
      setSelectedTicket(null);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCompanyId, dateRange, hideWithoutHours]);

  return (
    <div className="gap-2xl flex flex-1 flex-col">
      <Loader isLoading={isLoading} />
      <CreateTicketModal
        open={newModalOpen}
        onOpenChange={setNewModalOpen}
        onCreate={createTicket}
        isCreating={isSaving}
      />
      {selectedTicket && (
        <UpdateTicketModal
          open={updateModalOpen}
          onOpenChange={(open) => {
            setUpdateModalOpen(open);
            if (!open) setSelectedTicket(null);
          }}
          ticket={selectedTicket}
          onUpdate={updateTicket}
          isUpdating={isSaving}
        />
      )}
      <SyncTicketsModal open={syncModalOpen} onOpenChange={setSyncModalOpen} />

      <header className="flex w-full items-center justify-between">
        <Text as="h1" variant="heading">
          {t('tickets.title')}
        </Text>
        <div className="gap-md flex items-center">
          {lastTicketsSync && (
            <Text variant="label" size="sm" className="text-neutral-muted">
              {t('tickets.lastSync')}{' '}
              {formatDistanceToNow(new Date(lastTicketsSync), { addSuffix: true, locale: ptBR })}
            </Text>
          )}
          <Button variant="secondary" onClick={() => setSyncModalOpen(true)} disabled={isLoading}>
            <RefreshCwIcon size={20} />
            {t('common.sync')}
          </Button>
          <Button onClick={() => setNewModalOpen(true)} disabled={isLoading}>
            <PlusIcon size={20} />
            {t('common.create')}
          </Button>
        </div>
      </header>

      <div>
        <div className="gap-xs flex items-center">
          <Select
            options={companyOptions}
            placeholder={t('tickets.filterByCompany')}
            width={276}
            value={selectedCompanyId === 'all' ? '' : selectedCompanyId}
            onValueChange={(value) => setSelectedCompanyId(value || 'all')}
          />
          <DatePicker
            placeholder={t('tickets.filterByDate')}
            width={276}
            mode="range"
            value={dateRange}
            onChange={(date) => setDateRange(date as DateRange | undefined)}
          />
          <div className="flex items-center gap-2">
            <Switch checked={hideWithoutHours} onCheckedChange={setHideWithoutHours} />
            <Text variant="label" size="sm">
              {t('tickets.hideWithoutHours')}
            </Text>
          </div>
        </div>

        <div className="mt-md gap-md grid grid-cols-3">
          {tickets.length > 0
            ? tickets.map((ticket) => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  onEdit={() => handleEditTicket(ticket)}
                  onDelete={() => confirmDeleteTicket(ticket.id)}
                />
              ))
            : !isLoading && (
                <div className="col-span-3 py-8 text-center">
                  <Text variant="label" className="text-neutral-muted">
                    {t('common.tableEmpty')}
                  </Text>
                </div>
              )}
        </div>
        <Pagination
          className="mt-lg"
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          disabled={isLoading}
        />
      </div>
    </div>
  );
}
